/**
 * Meta Connection Context
 * Provides app-wide Meta connection status for persistent banner display
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '@/context/AuthContext';
import { useMetaConnection } from '@/hooks/useMetaConnection';

type ConnectionStatus = 'connected' | 'expired' | 'error' | 'disconnected' | 'loading';

interface MetaConnectionError {
  code: string;
  message: string;
  recoveryAction: 'reconnect' | 'retry';
}

interface MetaConnectionState {
  status: ConnectionStatus;
  error?: MetaConnectionError;
  showBanner: boolean;
  dismissBanner: () => void;
  metaUserName?: string;
  adAccountName?: string;
}

const MetaConnectionContext = createContext<MetaConnectionState | null>(null);

export function useMetaConnectionStatus(): MetaConnectionState {
  const context = useContext(MetaConnectionContext);
  if (!context) {
    throw new Error('useMetaConnectionStatus must be used within MetaConnectionProvider');
  }
  return context;
}

interface MetaConnectionProviderProps {
  children: ReactNode;
}

export function MetaConnectionProvider({ children }: MetaConnectionProviderProps) {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  const { data: connection, isLoading, error } = useMetaConnection(teamId ?? '');
  const [dismissed, setDismissed] = useState(false);

  // Track previous connection status to reset dismissed state on status change
  const prevStatusRef = useRef<string | undefined>();

  // Reset dismissed state when connection status changes
  useEffect(() => {
    const currentStatus = connection?.status;
    const prevStatus = prevStatusRef.current;

    if (prevStatus !== currentStatus) {
      // Reset dismissed if connection was restored (became active)
      // or went from active to an error state
      const wasActive = prevStatus === 'active';
      const isActive = currentStatus === 'active';

      if (isActive || wasActive) {
        setDismissed(false);
      }
      prevStatusRef.current = currentStatus;
    }
  }, [connection?.status]);

  const state: MetaConnectionState = useMemo(() => {
    const dismissBanner = () => setDismissed(true);

    if (isLoading) {
      return {
        status: 'loading',
        showBanner: false,
        dismissBanner,
      };
    }

    if (error || !connection) {
      return {
        status: 'disconnected',
        showBanner: false,
        dismissBanner,
      };
    }

    if (connection.status === 'active') {
      return {
        status: 'connected',
        showBanner: false,
        dismissBanner,
        metaUserName: connection.metaUserName,
        adAccountName: connection.metaAdAccountName,
      };
    }

    if (connection.status === 'expired') {
      return {
        status: 'expired',
        error: {
          code: 'META_TOKEN_EXPIRED',
          message: 'Your Meta connection expired. Please reconnect.',
          recoveryAction: 'reconnect',
        },
        showBanner: !dismissed,
        dismissBanner,
        metaUserName: connection.metaUserName,
        adAccountName: connection.metaAdAccountName,
      };
    }

    if (connection.status === 'error' || connection.status === 'revoked') {
      return {
        status: 'error',
        error: {
          code: 'META_CONNECTION_ERROR',
          message: 'There was a problem with your Meta connection. Please reconnect.',
          recoveryAction: 'reconnect',
        },
        showBanner: !dismissed,
        dismissBanner,
        metaUserName: connection.metaUserName,
        adAccountName: connection.metaAdAccountName,
      };
    }

    return {
      status: 'disconnected',
      showBanner: false,
      dismissBanner,
    };
  }, [connection, isLoading, error, dismissed]);

  return (
    <MetaConnectionContext.Provider value={state}>
      {children}
    </MetaConnectionContext.Provider>
  );
}
